import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, Briefcase, MessageSquare, CreditCard, LogOut, Menu, ShieldAlert, Calendar, CalendarDays } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, signOut } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasPendingPayments, setHasPendingPayments] = useState(false);
  const [hasPendingBookings, setHasPendingBookings] = useState(false);
  const [hasInterviewApps, setHasInterviewApps] = useState(false);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const [messagesRes, paymentsRes, bookingsRes, appsRes] = await Promise.all([
        supabase.from('messages').select('user_id').eq('is_from_admin', false).eq('admin_has_replied', false),
        supabase.from('payments').select('id').eq('status', 'pending'),
        supabase.from('bookings').select('id').eq('status', 'pending'),
        supabase.from('applications').select('id').eq('status', 'Interview'),
      ]);

      if (!messagesRes.error) {
        const uniqueUsers = new Set((messagesRes.data || []).map(m => m.user_id));
        setUnreadCount(uniqueUsers.size);
      }
      setHasPendingPayments((paymentsRes.data?.length || 0) > 0);
      setHasPendingBookings((bookingsRes.data?.length || 0) > 0);
      setHasInterviewApps((appsRes.data?.length || 0) > 0);
    } catch (err) {
      console.error('Error fetching admin notifications:', err);
    }
  };

  const navigation = [
    { name: 'Overview', href: '/admin', icon: LayoutDashboard },
    { name: 'Users', href: '/admin/users', icon: Users },
    { name: 'Applications', href: '/admin/applications', icon: Briefcase },
    { name: 'Bookings', href: '/admin/bookings', icon: Calendar },
    { name: 'Calendar', href: '/admin/calendar', icon: CalendarDays },
    { name: 'Messages', href: '/admin/messages', icon: MessageSquare },
    { name: 'Payments', href: '/admin/payments', icon: CreditCard },
  ];

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  const SidebarContent = () => (
    <>
      <div className="h-16 flex items-center px-6 border-b border-indigo-900/50 bg-indigo-900/20">
        <Link to="/admin" className="flex items-center gap-2" onClick={() => setSidebarOpen(false)}>
          <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center">
            <ShieldAlert className="w-5 h-5 text-indigo-950" />
          </div>
          <span className="text-xl font-bold text-white">Admin Portal</span>
        </Link>
      </div>
      
      <div className="flex-1 py-6 px-4 space-y-1">
        {navigation.map((item) => {
          const isActive = location.pathname === item.href;
          let dotColor = null;
          if (item.name === 'Payments' && hasPendingPayments) dotColor = 'bg-rose-500 animate-pulse';
          else if (item.name === 'Bookings' && hasPendingBookings) dotColor = 'bg-amber-400';
          else if (item.name === 'Applications' && hasInterviewApps) dotColor = 'bg-emerald-500';

          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center justify-between px-4 py-3 rounded-xl transition-colors ${
                isActive 
                  ? 'bg-amber-500 text-indigo-950 font-bold' 
                  : 'text-indigo-200 hover:text-white hover:bg-indigo-800'
              }`}
            >
              <div className="flex items-center gap-3">
                <item.icon className={`w-5 h-5 ${isActive ? 'text-indigo-950' : ''}`} />
                {item.name}
              </div>
              {item.name === 'Messages' && unreadCount > 0 ? (
                <span className={`flex items-center justify-center w-6 h-6 text-xs font-bold rounded-full ${
                  isActive ? 'bg-indigo-950 text-white' : 'bg-rose-500 text-white'
                }`}>
                  {unreadCount}
                </span>
              ) : dotColor ? (
                <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${dotColor}`} />
              ) : null}
            </Link>
          );
        })}
      </div>

      <div className="p-4 border-t border-indigo-900/50">
        <div className="flex items-center gap-3 px-4 py-3 mb-2">
          <div className="w-9 h-9 rounded-full bg-amber-500 flex items-center justify-center text-indigo-950 font-bold text-sm">
            AD
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-white truncate">Administrator</p>
            <p className="text-xs text-indigo-300 truncate">System Access</p>
          </div>
        </div>
        <button 
          onClick={handleLogout}
          className="flex items-center gap-3 px-4 py-3 w-full rounded-xl text-indigo-300 hover:text-white hover:bg-indigo-800 transition-colors"
        >
          <LogOut className="w-5 h-5" />
          Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <div className={`fixed inset-y-0 left-0 w-64 bg-indigo-950 text-white flex flex-col z-50 transform transition-transform duration-300 ease-in-out md:hidden ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <SidebarContent />
      </div>

      {/* Desktop Sidebar */}
      <div className="w-64 bg-indigo-950 text-white flex-col hidden md:flex border-r border-indigo-900/50">
        <SidebarContent />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Navbar */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6 shadow-sm">
          <button 
            onClick={() => setSidebarOpen(true)}
            className="md:hidden p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="font-bold text-indigo-950 md:hidden">Admin Portal</div>
          <div className="flex-1 hidden md:block">
            <span className="bg-amber-100 text-amber-800 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
              Admin Mode
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-sm font-medium text-indigo-600 hover:text-indigo-800">
              Go to User Dashboard
            </Link>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6 md:p-8 bg-slate-100">
          <div className="max-w-6xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
